import { createInstance, type i18n } from "i18next";
import { initReactI18next } from "react-i18next";
import resourcesToBackend from "i18next-resources-to-backend";

export const SUPPORTED_LANGS = ["en", "ar", "tr"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

export function isRTL(lang: string) {
  return /^ar\b|^he\b|^fa\b|^ur\b/i.test(lang);
}

let initialized: i18n | null = null;

export function setupI18n(): i18n {
  if (initialized) return initialized;
  const inst = createInstance();
  const localeImports = import.meta.glob("./locales/*/*.json");

  inst
    .use(initReactI18next)
    .use(
      resourcesToBackend((lng: string, ns: string) => {
        const key = `./locales/${lng}/${ns}.json`;
        const loader = localeImports[key];
        return loader
          ? loader()
          : Promise.reject(new Error(`Translation not found: ${key}`));
      }),
    )
    .init({
      lng: getInitialLang(),
      fallbackLng: "en",
      supportedLngs: SUPPORTED_LANGS as unknown as string[],
      ns: ["common", "nav", "forms", "errors", "integrations", "pages", "ui"],
      defaultNS: "common",
      interpolation: { escapeValue: false },
      react: { useSuspense: true },
    });
  initialized = inst;
  // Set initial dir/lang attributes
  try {
    const lang = inst.language || "en";
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL(lang) ? "rtl" : "ltr";
  } catch {}
  return inst;
}

// Eagerly initialize default instance so getI18n() is never null when useTranslation is called
export const defaultI18n = setupI18n();

export function translateUi(
  key: string,
  defaultValue: string,
  values?: Record<string, unknown>,
): string {
  return defaultI18n.t(`ui:${key}`, {
    defaultValue,
    ...values,
  });
}

export function changeLanguage(lang: Lang) {
  const i18n = setupI18n();
  i18n.changeLanguage(lang);
  try {
    window.localStorage.setItem("storizone:lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL(lang) ? "rtl" : "ltr";
  } catch {}
}

export function getInitialLang(): Lang {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem("storizone:lang");
    if (saved && SUPPORTED_LANGS.includes(saved as Lang)) return saved as Lang;
  } catch {}
  const nav = navigator.language || "en";
  if (nav.startsWith("ar")) return "ar";
  if (nav.startsWith("tr")) return "tr";
  return "en";
}
